// Ids that identify a record. Transaction and Property are optional on a request, so an
// absent id is a normal state — but the backend reports the absent transaction id as the
// display placeholder "N/A" (`main_avi.py`: `req.Transaction_ID or "N/A"`), and older
// endpoints have reported "None". Both are non-empty strings, so every `if (!transactionId)`
// guard downstream passed and the app behaved as though a transaction existed: the
// "No Transaction" option was hidden, the Counterparty requirement warning never fired, a
// new request was not recognised as new (leaving the previous request's data on screen), and
// fetches were issued with a placeholder id.
//
// Normalising here — the one place every id passes through — fixes all of those at once and
// keeps future callers safe, instead of repeating a guard at ~49 call sites.
const ID_KEYS = ["transactionId", "entityId", "propertyId"];

// "0" is deliberately absent: record ids start at 1, but treating a genuine 0 as absent
// would trade this bug for a new one. requestId is absent too — no endpoint placeholders it.
const BLANK_ID_VALUES = ["", "none", "n/a", "null", "undefined"];

export const cleanId = (value) =>
  value == null || BLANK_ID_VALUES.includes(String(value).trim().toLowerCase())
    ? null
    : value;

class SharedData {
  static #instance;
  #data = {};
  #subscribers = new Map();

  constructor() {
    if (SharedData.#instance) {
      return SharedData.#instance;
    }
    
    this.#loadFromStorage();
    SharedData.#instance = this;
  }

  #loadFromStorage() {
    try {
      const storedData = localStorage.getItem('sharedData');
      this.#data = storedData ? JSON.parse(storedData) : {};
      // Sessions that ran before ids were normalised still hold "N/A" in localStorage.
      // Clean those on load, otherwise the stale placeholder survives until the user
      // re-enters a request from the dashboard.
      let changed = false;
      ID_KEYS.forEach((key) => {
        if (key in this.#data && this.#data[key] !== cleanId(this.#data[key])) {
          this.#data[key] = cleanId(this.#data[key]);
          changed = true;
        }
      });
      if (changed) this.#saveToStorage();
    } catch (error) {
      console.error('Error loading shared data from localStorage:', error);
      this.#data = {};
    }
  }

  #saveToStorage() {
    try {
      localStorage.setItem('sharedData', JSON.stringify(this.#data));
    } catch (error) {
      console.error('Error saving shared data to localStorage:', error);
    }
  }

  subscribe(key, callback) {
    if (!this.#subscribers.has(key)) {
      this.#subscribers.set(key, new Set());
    }
    
    const subscribers = this.#subscribers.get(key);
    subscribers.add(callback);
    
    // Immediately send current value to new subscriber
    callback(this.get(key));
    
    return () => this.unsubscribe(key, callback);
  }

  unsubscribe(key, callback) {
    if (this.#subscribers.has(key)) {
      this.#subscribers.get(key).delete(callback);
    }
  }

  // Auth survives a request switch; every cached form and id does not.
  static #KEEP_ON_SWITCH = ["token", "refreshToken", "userName", "userEmail"];

  // Opening a different request invalidates everything cached for the previous one —
  // clientFormData, corporateFormData, managers, riskFormData, and our own ids and
  // section statuses. Leaving them behind let one deal's managers save onto another's
  // entity, and one deal's "Validated" lock a fresh section.
  //
  // Guarded here because this is the one place all eleven `set("requestId", …)` call
  // sites route through, and the previous attempt — a hand-listed subset of keys at two
  // of those sites — is exactly what missed riskFormData and shipped the bug.
  #clearForRequestSwitch() {
    const keep = SharedData.#KEEP_ON_SWITCH
      .map((k) => [k, localStorage.getItem(k)])
      .filter(([, v]) => v !== null);

    localStorage.clear();
    keep.forEach(([k, v]) => localStorage.setItem(k, v));

    this.#data = {};
  }

  set(key, value) {
    if (ID_KEYS.includes(key)) value = cleanId(value);

    const currentValue = this.#data[key];
    if (currentValue === value) return;

    if (key === "requestId" && currentValue !== undefined && currentValue !== null) {
      this.#clearForRequestSwitch();
    }

    this.#data[key] = value;
    this.#saveToStorage();

    if (this.#subscribers.has(key)) {
      this.#subscribers.get(key).forEach(callback => {
        try {
          callback(value, currentValue);
        } catch (error) {
          console.error('Error in subscriber callback:', error);
        }
      });
    }
  }

  get(key) {
    return key ? this.#data[key] : {...this.#data};
  }

  delete(key) {
    if (!(key in this.#data)) return;

    const value = this.#data[key];
    delete this.#data[key];
    this.#saveToStorage();

    if (this.#subscribers.has(key)) {
      this.#subscribers.get(key).forEach(callback => {
        try {
          callback(undefined, value);
        } catch (error) {
          console.error('Error in subscriber callback:', error);
        }
      });
    }
  }

  clear() {
    const oldData = {...this.#data};
    this.#data = {};
    this.#saveToStorage();

    // Notify all subscribers of clearance
    this.#subscribers.forEach((callbacks, key) => {
      callbacks.forEach(callback => {
        try {
          callback(undefined, oldData[key]);
        } catch (error) {
          console.error('Error in subscriber callback:', error);
        }
      });
    });
  }
}

// Singleton instance
export const sharedData = new SharedData();